import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Table, Spinner, Badge } from 'react-bootstrap';
import dayjs from 'dayjs';
import { fetchSalesServiceById } from '../../../services/salesServiceService';

// Define Service Interface
interface SalesInterface {
  id: number;
  service_name: string;
  description: string;
  CDC_FLAG: string;
  created_at: string;
  plans: PlanInterface[];
}

// Define Plan Interface
interface PlanInterface {
  plan_id: number;
  plan_name: string;
  pricing: string;
  billing_period: string;
  plan_type: string;
  tier: string;
  max_users: number | null;
  valid_from: string;
  valid_to: string;
  CDC_FLAG: string;
}

const ServiceDetails = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [service, setService] = useState<SalesInterface | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadServiceDetails = async () => {
      try {
        const serviceData = await fetchSalesServiceById(serviceId);
        setService(serviceData);
      } catch (err) {
        setError('Failed to fetch service details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadServiceDetails();
  }, [serviceId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
        <p className="ms-2">Loading Service Details...</p>
      </div>
    );
  }

  if (!service) {
    return <p className="text-danger">Error: {'Service not found'}</p>;
  }

  // Extract active plans (valid_to is set to "9999-12-31 23:59:59")
  const activePlans = service.plans?.filter(
    (plan: PlanInterface) => plan.valid_to === '9999-12-31 23:59:59'
  );

  // Extract history plans
  const historyPlans = service.plans?.filter(
    (plan: PlanInterface) => plan.valid_to !== '9999-12-31 23:59:59'
  );

  return (
    <div>
      {/* 🔹 Service Details */}
      <Card className="mb-4 shadow">
        <Card.Body>
          <h4 className="fw-bold">{service.service_name}</h4>
          <p>{service.description}</p>
          <p>
            <strong>Status:</strong>{' '}
            <Badge bg={service.CDC_FLAG === 'A' ? 'success' : 'secondary'}>
              {service.CDC_FLAG === 'A' ? 'Active' : 'Inactive'}
            </Badge>
          </p>
          <small className="text-muted">
            Created At:{' '}
            {dayjs(service.created_at).format('DD/MM/YYYY HH:mm:ss')}
          </small>
        </Card.Body>
      </Card>

      {/* 🔹 Active Plans */}
      {activePlans.length > 0 && (
        <Card className="mb-4 shadow">
          <Card.Body>
            <h5 className="fw-bold">Active Plans</h5>
            {activePlans.map((plan: PlanInterface) => (
              <div key={plan.plan_id} className="mb-3">
                <p>
                  <strong>Plan Name:</strong> {plan.plan_name}
                </p>
                <p>
                  <strong>Pricing:</strong> ₹
                  {parseFloat(plan.pricing).toLocaleString()}
                </p>
                <p>
                  <strong>Billing Period:</strong> {plan.billing_period}
                </p>
                <p>
                  <strong>Plan Type:</strong> {plan.plan_type}
                </p>
                <p>
                  <strong>Tier:</strong> {plan.tier}
                </p>
                {plan.max_users !== null && (
                  <p>
                    <strong>Max Users:</strong> {plan.max_users}
                  </p>
                )}
                <small className="text-muted">
                  Active From:{' '}
                  {dayjs(plan.valid_from).format('DD/MM/YYYY HH:mm:ss')}
                </small>
                <hr />
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {/* 🔹 Pricing History */}
      <Card className="mb-4 shadow">
        <Card.Body>
          <h5 className="fw-bold">Plan Pricing History</h5>
          {historyPlans.length > 0 ? (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Plan ID</th>
                  <th>Plan Name</th>
                  <th>Pricing</th>
                  <th>Billing Period</th>
                  <th>Plan Type</th>
                  <th>Tier</th>
                  <th>Max Users</th>
                  <th>Valid From</th>
                  <th>Valid To</th>
                </tr>
              </thead>
              <tbody>
                {historyPlans.map((plan: PlanInterface) => (
                  <tr key={plan.plan_id}>
                    <td>{plan.plan_id}</td>
                    <td>{plan.plan_name}</td>
                    <td>₹{parseFloat(plan.pricing).toLocaleString()}</td>
                    <td>{plan.billing_period}</td>
                    <td>{plan.plan_type}</td>
                    <td>{plan.tier}</td>
                    <td>{plan.max_users !== null ? plan.max_users : 'N/A'}</td>
                    <td>
                      {dayjs(plan.valid_from).format('DD/MM/YYYY HH:mm:ss')}
                    </td>
                    <td>
                      {dayjs(plan.valid_to).format('DD/MM/YYYY HH:mm:ss')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">No history records found.</p>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ServiceDetails;
